import { AxiosError } from "axios";

/**
 * Extract a user-friendly error message from any error object.
 * Prioritizes backend error messages, then falls back to status code messages,
 * then to the provided fallback, and finally to a generic message.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  // 1. Check for AxiosError with backend message
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    const backendMsg = data?.message || data?.error;
    if (typeof backendMsg === "string" && backendMsg.trim()) {
      return backendMsg;
    }
    // Map common status codes to friendly messages
    return (
      getStatusCodeMessage(error.response?.status) ??
      fallback ??
      "Something went wrong. Please try again."
    );
  }
  // 2. Standard Error
  if (error instanceof Error && error.message) {
    // Filter out raw Axios messages like "Request failed with status code 400"
    if (!error.message.startsWith("Request failed with status code")) {
      return error.message;
    }
  }
  return fallback ?? "Something went wrong. Please try again.";
}

/**
 * Map HTTP status codes to user-friendly error messages.
 */
function getStatusCodeMessage(status?: number): string | null {
  switch (status) {
    case 400:
      return "The request was invalid. Please check your input.";
    case 401:
      return "Your session has expired. Please log in again.";
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return "The requested resource was not found.";
    case 409:
      return "There is a conflict. The operation is already in progress.";
    case 413:
      return "The file is too large. Please try a smaller file.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 500:
      return "A server error occurred. Please try again later.";
    case 502:
    case 503:
    case 504:
      return "The server is temporarily unavailable.";
    default:
      return null;
  }
}
